import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

import type { Medication } from './MedicationsDataBlock';
import MedicationTimeline from './MedicationTimeline';

interface MedicationTimelineBlockProps {
  medications?: Medication[];
  isLoading?: boolean;
  title?: string;
  description?: string;
  emptyMessage?: string;
  hideHeader?: boolean;
  containerSx?: SxProps<Theme>;
}

const MedicationTimelineBlock: React.FC<MedicationTimelineBlockProps> = ({
  medications,
  isLoading = false,
  title = 'Medication Timeline',
  description,
  emptyMessage = 'No medication history available.',
  hideHeader = false,
  containerSx,
}) => {
  const combinedContainerSx = useMemo<SxProps<Theme>>(() => {
    if (containerSx) {
      return [{ width: '100%' }, containerSx] as SxProps<Theme>;
    }
    return { width: '100%' };
  }, [containerSx]);

  const hasData = Boolean(medications && medications.length > 0);

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            py: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (!hasData) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: hideHeader ? 0 : 2 }}>
        <MedicationTimeline medications={medications as Medication[]} />
      </Box>
    );
  };

  return (
    <Box sx={combinedContainerSx}>
      {!hideHeader && (
        <>
          <Typography variant="h5" sx={{ mb: 1.5 }}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {description}
            </Typography>
          )}
        </>
      )}
      {renderBody()}
    </Box>
  );
};

export default MedicationTimelineBlock;

